import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  input,
  output,
  viewChild
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EditorCanvasRenderer, ObjectDetectionBox } from '../../infrastructure/rendering';
import { Size2D } from '../../domain/interfaces/shared';
import { PreviewImageLoadedMessage } from '../../domain/models/messages';
import { AngularResizeEventModule, ResizedEvent } from 'angular-resize-event';
import { MessagingService } from '../../infrastructure/messaging/messaging.service';
import { AppError, EditorArrangeTileInput } from '@imagise/entities';
import { SpinnerComponent } from '@imagise/shared';
import {
  EMPTY_EDITOR_CANVAS_VIEW_STATE,
  EditorCanvasViewState,
  EditorFormStore,
} from '../../domain';

@Component({
  selector: 'lib-svg-canvas',
  templateUrl: './svg-canvas.component.html',
  styleUrls: ['./svg-canvas.component.scss'],
  imports: [AngularResizeEventModule, SpinnerComponent],
})
export class SvgCanvasComponent implements OnDestroy, AfterViewInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly messageBus = inject(MessagingService);
  private readonly editorStore = inject(EditorFormStore);
  private readonly changeDetector = inject(ChangeDetectorRef);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private canvasWidth = 640;

  private canvasHeight = 480;

  private _viewState: EditorCanvasViewState = EMPTY_EDITOR_CANVAS_VIEW_STATE;

  private _isActive = false;
  @Input() set isActive(value: boolean) {
    if (value === this.isActive) return;
    this._isActive = value;
    if (value && this.renderer != null) {
      this.renderer.render();
    }
  }
  get isActive(): boolean {
    return this._isActive;
  }

  /**
   * Image URL
   */
  private _imageUrl?: string;
  @Input() set imageUrl(value: string | undefined) {
    this._imageUrl = value;
    this.initImage();
  }
  get imageUrl(): string | undefined {
    return this._imageUrl;
  }

  @Input() set viewState(value: EditorCanvasViewState) {
    this._viewState = value;
    this.applyViewState();
  }
  get viewState(): EditorCanvasViewState {
    return this._viewState;
  }

  private _arrangeTiles: readonly EditorArrangeTileInput[] | null = null;
  /** Draggable tiles of the arrange mode; null restores the regular view. */
  @Input() set arrangeTiles(value: readonly EditorArrangeTileInput[] | null) {
    this._arrangeTiles = value;
    this.renderer?.setArrangeTiles(value);
  }
  get arrangeTiles(): readonly EditorArrangeTileInput[] | null {
    return this._arrangeTiles;
  }

  private _objectDetectionBoxes: readonly ObjectDetectionBox[] = [];
  @Input() set objectDetectionBoxes(value: readonly ObjectDetectionBox[]) {
    this._objectDetectionBoxes = value;
    this.renderer?.setObjectDetectionBoxes(value);
  }

  readonly arrangeTileMoved = output<{ id: string; x: number; y: number }>();

  readonly previewLoading = input(false);

  readonly nativeAnimatedImageUrl = input<string | undefined>(undefined);

  /**
   * The error object
   */
  @Input() error?: AppError;

  readonly root = viewChild.required<ElementRef<HTMLElement>>('root');

  private renderer: EditorCanvasRenderer | null = null;

  /**
   * Hide image flag
   * @returns {}
   */
  get hideImage(): boolean {
    return this.error != null;
  }

  get imageSize(): Size2D | null {
    return this.renderer ? this.renderer.imageSize : null;
  }

  get hasNativeAnimatedImage(): boolean {
    return Boolean(this.nativeAnimatedImageUrl()) && !this.hideImage;
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.renderer = new EditorCanvasRenderer(this.root());
    this.renderer.imageLoadedCallback = () => {
      this.messageBus.publish(new PreviewImageLoadedMessage());
      this.changeDetector.markForCheck();
    };
    this.renderer.onCropRectChanged = rect => {
      this.editorStore.setDraftCropRect(rect);
    };
    this.renderer.onArrangeTileMoved = (id, x, y) => {
      this.arrangeTileMoved.emit({ id, x, y });
    };
    this.renderer.resize(this.canvasWidth, this.canvasHeight);
    this.renderer.setViewState(this._viewState);
    this.renderer.setArrangeTiles(this._arrangeTiles);
    this.renderer.setObjectDetectionBoxes(this._objectDetectionBoxes);
    this.initImage();
  }

  ngOnDestroy(): void {
    this.renderer?.dispose();
    this.renderer = null;
  }

  /**
   * On root element resized event handler
   * @param event
   */
  onRootResized(event: ResizedEvent) {
    this.canvasWidth = event.newRect.width;
    this.canvasHeight = event.newRect.height;

    if (this.renderer) {
      this.renderer.resize(this.canvasWidth, this.canvasHeight);
      if (this.isActive) {
        this.renderer.render();
      }
    }
  }

  getPreviewData(): string {
    return this.renderer?.exportToSvg() ?? '';
  }

  get canExportCommittedPng(): boolean {
    return Boolean(this.imageUrl) && !this.previewLoading() && !this.hideImage &&
      !this.hasNativeAnimatedImage && Boolean(this.renderer?.canExportCommittedPng);
  }

  exportCommittedPngBase64(): string | null {
    return this.canExportCommittedPng ? this.renderer?.exportCommittedPngBase64() ?? null : null;
  }

  private initImage() {
    if (!this.renderer || !this.imageUrl) return;
    this.renderer.imageUrl = this.imageUrl;
  }

  private applyViewState(): void {
    this.renderer?.setViewState(this._viewState);
  }
}
